import React, { useMemo } from "react";
import { AgCharts } from "ag-charts-react";
import TrendDataTable from "./TrendDataTable.js";
import { formatCurrency, formatUOM } from "../aabiFormatters";
import {
  AABI_CHART_COLORS,
  getAabiChartBaseOptions,
  prefersReducedMotion,
} from "../aabiChartOptions";
import { buildTrendSummary, formatCompactCurrency } from "../aabiViewModel";
import { MEASURE_LABELS } from "../aabiConstants";
import "./../AABI.css";

const axisLabelTheme = {
  fontFamily: "Figtree, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
  fontSize: 12,
  color: AABI_CHART_COLORS.text,
};

const bucketValue = (bucket, mode, measure) =>
  mode === "uom"
    ? bucket?.uom ?? 0
    : measure === "realized"
      ? bucket?.realizedRevenue ?? 0
      : bucket?.currentRevenue ?? 0;

// The chart/data body of a revenue trend (§6.1): the selected period as a solid
// primary line with the preceding period overlaid as a muted dashed line,
// aligned by bucket ordinal, or the equivalent semantic table. Extracted from
// PerformanceTrendChart so the manufacturer page and the Executive Dashboard
// draw the same trend; it owns no fetching and no panel chrome.
//
//   onBucketClick  (bucket) => void; makes chart points clickable
//   bucketLink     (bucket) => url; makes Data view rows links
//   dataMeasure    when set, the Data view follows the selected measure
//                  (the manufacturer page keeps its Current-only table)
const TrendChartBody = ({
  trend,
  measure,
  mode,
  view,
  onBucketClick,
  bucketLink,
  dataMeasure,
  chartHeight = 300,
}) => {
  const summary = useMemo(() => buildTrendSummary(trend, measure), [trend, measure]);

  const chartData = useMemo(() => {
    const current = trend?.currentSeries ?? [];
    const prior = trend?.priorSeries ?? [];
    return current.map((bucket, index) => ({
      bucketIndex: index,
      label: bucket.label,
      Current: bucketValue(bucket, mode, measure),
      Prior: prior[index] ? bucketValue(prior[index], mode, measure) : null,
      currentFull:
        mode === "uom"
          ? formatUOM(bucket.uom)
          : formatCurrency(bucketValue(bucket, mode, measure)),
      priorFull: prior[index]
        ? mode === "uom"
          ? formatUOM(prior[index].uom)
          : formatCurrency(bucketValue(prior[index], mode, measure))
        : null,
    }));
  }, [trend, mode, measure]);

  const valueLabel = mode === "uom" ? "UOM" : MEASURE_LABELS[measure];

  const chartOptions = useMemo(() => {
    const base = getAabiChartBaseOptions({
      reducedMotion: prefersReducedMotion(),
    });
    return {
      ...base,
      data: chartData,
      axes: [
        { type: "category", position: "bottom", label: axisLabelTheme },
        {
          type: "number",
          position: "left",
          label: {
            ...axisLabelTheme,
            // Home/SalesChart formats its value axis the same way; without it
            // the axis prints raw ticks like "60000" beside a page whose every
            // other figure is compact currency.
            formatter: ({ value }) =>
              mode === "uom" ? formatUOM(value) : formatCompactCurrency(value),
          },
          gridLine: {
            style: [{ stroke: AABI_CHART_COLORS.gridLine, lineDash: [4, 4] }],
          },
        },
      ],
      series: [
        {
          type: "line",
          xKey: "label",
          yKey: "Current",
          yName: `${valueLabel} (selected period)`,
          stroke: AABI_CHART_COLORS.primary,
          strokeWidth: 2,
          marker: { enabled: chartData.length <= 16, size: 4 },
          cursor: onBucketClick ? "pointer" : undefined,
          tooltip: {
            // v11 result shape: { heading, title, data: [{ label, value }] }.
            // `content` is not part of it and is silently ignored. No `title`:
            // AG Charts already heads the tooltip with the bucket, so setting
            // one printed "Jul 2026" twice.
            renderer: ({ datum }) => ({
              data: [{ label: valueLabel, value: datum.currentFull }],
            }),
          },
          listeners: onBucketClick
            ? {
                nodeClick: ({ datum }) => {
                  const bucket = trend?.currentSeries?.[datum?.bucketIndex];
                  if (bucket) onBucketClick(bucket);
                },
              }
            : undefined,
        },
        {
          type: "line",
          xKey: "label",
          yKey: "Prior",
          yName: `${valueLabel} (prior period)`,
          stroke: AABI_CHART_COLORS.muted,
          strokeWidth: 2,
          lineDash: [6, 4],
          marker: { enabled: false },
          tooltip: {
            renderer: ({ datum }) => ({
              data: [
                { label: `Prior ${valueLabel}`, value: datum.priorFull ?? "No prior activity" },
              ],
            }),
          },
        },
      ],
      legend: { enabled: true, position: "bottom" },
    };
  }, [chartData, valueLabel, mode, onBucketClick, trend]);

  return (
    <>
      {summary && (
        <p className="aabi-trend-summary" aria-live="polite">
          {summary}
        </p>
      )}

      {view === "chart" ? (
        <div
          className="aabi-chart-frame"
          role="img"
          aria-label={`${valueLabel} trend for the selected period with the preceding-period comparison as a dashed line. A Data view with the exact bucket table is available.`}
        >
          <AgCharts options={chartOptions} style={{ width: "100%", height: chartHeight }} />
        </div>
      ) : (
        <TrendDataTable
          trend={trend}
          mode={mode}
          measure={dataMeasure}
          bucketLink={bucketLink}
        />
      )}
    </>
  );
};

export default TrendChartBody;
